import { ApiPerson, ApiPersonCategory, ApiPersonType } from '../../api';
import React, { useState } from 'react';
import { Card, Col, Collapse, Row } from 'react-bootstrap';
import { Link } from 'react-router';
import {
  orderBySchemaFields,
  isChildField,
  isComplexField,
  formatArrayAttribute,
  formatObjectAttribute,
  formatArrayObjectAttribute,
} from '../../schema/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faUser, faUserNinja } from '@fortawesome/pro-solid-svg-icons';
import { faUserAlien } from '@fortawesome/pro-solid-svg-icons';

import './PersonAttributes.scss';
import PersonAttributeRow from './PersonAttributeRow';
import { isCounterparty } from '../../common/helper';
import ShowHideButton from '../../common/ShowHideButton';
import { InternalApiEntitySchemaFieldType, InternalApiEntityType, InternalApiPerson } from '../../api-internal';
import { useQueryEntitySchema } from '../../schema/entitySchemaActions';
import { SalvIconButton } from '@salvapp/ui-components';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { showFeedback } from '../../feedback/feedbackReducer';
import { copyToClipboard } from '../../common/clipboard';
import { useDispatch } from 'react-redux';

export interface PersonAttributesProps {
  person: ApiPerson | InternalApiPerson;
  displayCustomerLink?: boolean;
  highlightedFields?: string[];
}

export const PersonAttributes = ({ person, displayCustomerLink, highlightedFields }: PersonAttributesProps) => {
  const dispatch = useDispatch();
  const { data: personSchema } = useQueryEntitySchema(InternalApiEntityType.PERSON);
  const personSchemaFields = personSchema?.schemaFields;

  const [expanded, setExpanded] = useState(true);

  if (person === null) {
    return null;
  }
  const { attributes } = person;
  if (!attributes) {
    return null;
  }

  const getPersonIcon = () => {
    if (person.category === ApiPersonCategory.COUNTERPARTY) {
      return faUserAlien;
    }
    if (person.type === ApiPersonType.INDIVIDUAL || person.type === ApiPersonType.PRIVATE) {
      return faUser;
    }
    if (person.type === ApiPersonType.BUSINESS || person.type === ApiPersonType.LEGAL) {
      return faBriefcase;
    }
    return faUserNinja;
  };

  const personIcon = getPersonIcon();

  const orderedFieldNames = orderBySchemaFields(
    personSchemaFields?.filter((schemaField) => !isChildField(schemaField)),
    Object.keys(attributes)
  );

  const getPersonAsText = () => {
    let personAsText = '';

    for (const fieldName of orderedFieldNames) {
      const parentField = personSchemaFields?.find(
        (schemaField) => schemaField.fieldName === fieldName && !isChildField(schemaField)
      );
      const parentName = parentField?.displayName ?? fieldName;

      if (!parentField || !isComplexField(parentField)) {
        personAsText = personAsText.concat(`${parentName}\t${attributes[fieldName]}\n`);
      } else if (parentField.isMulti && parentField.type === InternalApiEntitySchemaFieldType.OBJECT) {
        personAsText = personAsText.concat(`${parentName}\n`);
        personAsText = personAsText.concat(
          formatArrayObjectAttribute(parentField, personSchemaFields, attributes[fieldName])
        );
      } else if (parentField.type === InternalApiEntitySchemaFieldType.OBJECT) {
        personAsText = personAsText.concat(`${parentName}\n`);
        personAsText = personAsText.concat(
          formatObjectAttribute(parentField, personSchemaFields, attributes[fieldName])
        );
      } else if (parentField.isMulti) {
        personAsText = personAsText.concat(
          `${parentName}\t${formatArrayAttribute(parentField, attributes[fieldName])}\n`
        );
      }
    }

    return personAsText;
  };

  return (
    <Card className={'person-attributes'}>
      <Card.Header className='cursor-pointer' onClick={() => setExpanded(!expanded)}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <div className={'title-s'}>{isCounterparty(person) ? 'Counterparty' : 'Customer'} details</div>
          <SalvIconButton
            ariaLabel={`Copy person`}
            icon={faCopy}
            className={'copy-button'}
            onClick={async () => {
              const copied = await copyToClipboard(getPersonAsText());
              dispatch(
                showFeedback(
                  copied
                    ? { message: 'Person copied', success: true }
                    : { message: 'Could not copy to clipboard', success: false }
                )
              );
            }}
          />
        </div>
        <ShowHideButton expanded={expanded} />
      </Card.Header>
      <Collapse in={expanded}>
        <Card.Body>
          <>
            <Row className='align-items-center attribute-row'>
              <Col>
                <h5 className='mb-0'>Type</h5>
              </Col>
              <Col md='auto'>
                <span className='small'>
                  {person.type} <FontAwesomeIcon icon={personIcon} aria-label={person.type} />
                </span>
              </Col>
            </Row>
            <div className={'horizontal-separator-deprecated'} />
          </>
          {orderedFieldNames.map((fieldName, i, a) => (
            <PersonAttributeRow
              key={i}
              fieldName={fieldName}
              index={i}
              array={a}
              attributes={attributes}
              personSchemaFields={personSchemaFields}
              highlightedFields={highlightedFields}
              counterparty={isCounterparty(person)}
            />
          ))}
          {displayCustomerLink ? (
            <React.Fragment>
              <Row className='align-items-center'>
                <Col>
                  <hr />
                  <Link to={`/customer/${person.id}`}>See full customer info in Salv</Link>
                </Col>
              </Row>
            </React.Fragment>
          ) : null}
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default PersonAttributes;
